import React, { useEffect } from 'react';
import {
  Colors,
  FontFamily,
  Input,
  Main,
  Models,
  PrimaryButton,
} from 'utils/imports.utils';
import Assets from 'imports/assets.import';
import './sign_up.screen.scss';
import { ButtonSize, ButtonTypes, GoogleAuthMethod } from 'utils/interface.utils';
import { useNavigate } from 'react-router-dom';
import { toastifySuccess, useSetState } from 'utils/functions.utils';
import Checkbox from 'common_components/ui/checkbox/checkbox.component';
import GoogleAuth from 'components/google_auth/google_auth.component';
import { STRINGS } from 'constants/strings.constant';

const ERROR_VISIBLE_MS = 5000;

const SignUp = () => {
  const navigate = useNavigate();

  const [state, setState] = useSetState({
    username: '',
    usernameError: '',
    email: '',
    emailError: '',
    password: '',
    passwordError: '',
    confirmPassword: '',
    confirmPasswordError: '',
    // orgNameError: "",
    activity: false,
    showPassword: false,
    showConfirmPassword: false,
    acceptedTerms: false,
    termsError: '',
    isDisabled: true,
  });

  useEffect(() => {
    const disabled =
      !state.username.trim() ||
      !state.email.trim() ||
      !state.password.trim() ||
      !state.confirmPassword.trim() ||
      state.activity;

    setState({ isDisabled: disabled });
  }, [state.username, state.email, state.password, state.confirmPassword, state.activity]);

  const handleSignUp = async (e: any) => {
    if (!state.acceptedTerms) {
      setState({ termsError: STRINGS.signUp.termsRequired });
      return;
    }
    if (state.password !== state.confirmPassword) {
      setState({ confirmPasswordError: STRINGS.signUp.passwordsDoNotMatch });
      return;
    }
    setState({ activity: true });

    try {
      const user = await Models.auth.signup({
        username: state.username,
        email: state.email,
        password: state.password,
        accepted_terms: true,
      });

      if (user) {
        toastifySuccess("Sign up successful!");
        navigate('/welcome'); // Redirect after successful signup
      }
    } catch (error) {
      console.error('Sign up error:', error);

      const message = typeof error === 'string' ? error : '';
      const validationPrefix = 'Error validating request body.';

      if (message === 'Email Already Exists') {
        setState({ emailError: message });
      } else if (message.startsWith(validationPrefix)) {
        // Joi message: 'Error validating request body. "password" length must be ...'
        const detail = message.slice(validationPrefix.length).trim();
        if (detail.includes('"password"')) {
          setState({ passwordError: detail });
        } else if (detail.includes('"email"')) {
          setState({ emailError: detail });
        } else if (detail.includes('"username"')) {
          setState({ usernameError: detail });
        } else {
          setState({ emailError: detail || 'Sign up failed. Please try again.' });
        }
      } else {
        setState({ emailError: 'Sign up failed. Please try again.' });
      }
    } finally {
      setState({ activity: false });
    }
  };

  // One message at the end of the form; the field(s) it refers to get a red border.
  const formError =
    state.usernameError || state.emailError || state.passwordError || state.confirmPasswordError || state.termsError;

  // The message and the red outline clear themselves after a few seconds.
  useEffect(() => {
    if (!formError) return;
    const timer = setTimeout(() => {
      setState({ usernameError: '', emailError: '', passwordError: '', confirmPasswordError: '', termsError: '' });
    }, ERROR_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [formError]);

  return (
    <Main
      backgroundColor={Colors.screenBackground}
      isRenderOnlyLogo={true}
      logoSize={{ width: '200px', height: '83px' }}>
      <div className="sign_up">
        <div className="sign_up_container">
          <div className="sign_up_header">
            <div className="sign_up_header_title">Sign up</div>
            <div className="sign_up_header_desc">
              Join us and get started in seconds
            </div>
          </div>
          <div className="sign_up_form">
            <div className="sign_up_form_container">
              <div className={`sign_up_form_input ${state.usernameError ? 'sign_up_form_input_error' : ''}`}>
                <Input
                  placeholder="Enter your full name"
                  label="Full name"
                  value={state.username}
                  onChange={(e) =>
                    setState({ username: e.target.value, usernameError: '' })
                  }
                  error={state.usernameError}
                />
              </div>
              <div className={`sign_up_form_input ${state.emailError ? 'sign_up_form_input_error' : ''}`}>
                <Input
                  placeholder="Enter your email id"
                  label="Email id"
                  type="email"
                  value={state.email}
                  onChange={(e) =>
                    setState({ email: e.target.value, emailError: '' })
                  }
                  error={state.emailError}
                />
              </div>
              <div className={`sign_up_form_input ${state.passwordError ? 'sign_up_form_input_error' : ''}`}>
                <Input
                  placeholder="Enter your password"
                  type={state.showPassword ? 'text' : 'password'}
                  label="Password"
                  value={state.password}
                  onChange={(e) =>
                    setState({
                      password: e.target.value,
                      passwordError: '',
                      confirmPasswordError: '',
                    })
                  }
                  icon={state.showPassword ? Assets.eye : Assets.eye_slash}
                  iconPosition="end"
                  iconOnPress={() =>
                    setState({ showPassword: !state.showPassword })
                  }
                  error={state.passwordError}
                />
              </div>
              <div className={`sign_up_form_input ${state.confirmPasswordError ? 'sign_up_form_input_error' : ''}`}>
                <Input
                  placeholder={STRINGS.signUp.confirmPasswordPlaceholder}
                  type={state.showConfirmPassword ? 'text' : 'password'}
                  label={STRINGS.signUp.confirmPasswordLabel}
                  value={state.confirmPassword}
                  onChange={(e) =>
                    setState({
                      confirmPassword: e.target.value,
                      confirmPasswordError: '',
                    })
                  }
                  icon={state.showConfirmPassword ? Assets.eye : Assets.eye_slash}
                  iconPosition="end"
                  iconOnPress={() =>
                    setState({ showConfirmPassword: !state.showConfirmPassword })
                  }
                />
              </div>
              <div className="sign_up_form_terms">
                <Checkbox
                  checked={state.acceptedTerms}
                  onChange={(checked) => setState({ acceptedTerms: checked, termsError: '' })}
                  label={
                    <>
                      {STRINGS.signUp.agreePrefix}
                      <a href={STRINGS.signUp.termsUrl} target="_blank" rel="noopener noreferrer">
                        {STRINGS.signUp.termsLinkText}
                      </a>
                      {STRINGS.signUp.agreeJoiner}
                      <a href={STRINGS.signUp.contentRulesUrl} target="_blank" rel="noopener noreferrer">
                        {STRINGS.signUp.contentRulesLinkText}
                      </a>
                    </>
                  }
                />
              </div>
              {formError && (
                <div className="input_field_error sign_up_form_error" role="alert">
                  <span className="sign_up_form_error_icon" aria-hidden="true">!</span>
                  <span>{formError}</span>
                </div>
              )}
            </div>
          </div>
          <div className="sign_up_buttons">
            <div className="sign_up_buttons_container">
              <div className="sign_up_buttons_signup">
                <PrimaryButton
                  onClick={(e) => handleSignUp(e)}
                  fontFamily={FontFamily.medium}
                  lineHeight="28px"
                  text="Sign up"
                  size={ButtonSize.LARGE}
                  disabled={state.isDisabled}
                  activity={state.activity}
                  type={
                    state.isDisabled
                      ? ButtonTypes.DISABLED
                      : ButtonTypes.PRIMARY_GRADIENT
                  }
                />
              </div>
              <div className="sign_up_buttons_separator_line">
                <div className="sign_up_buttons_separator_line_text">or</div>
              </div>
              <div className="sign_up_buttons_google">
                <GoogleAuth
                  method={GoogleAuthMethod.SIGNUP}
                  acceptedTerms={state.acceptedTerms}
                  onTermsMissing={() => setState({ termsError: STRINGS.signUp.termsRequired })}
                />
              </div>
              <div className="sign_up_buttons_already_have_account">
                <div className="sign_up_buttons_already_have_account_text">
                  Already have an account?
                </div>
                <PrimaryButton
                  onClick={() => navigate('/login')}
                  color={Colors.primaryTextColor}
                  fontFamily={FontFamily.semiBold}
                  lineHeight="24px"
                  textDecorationColor={Colors.primaryTextColor}
                  text="Log in"
                  type={ButtonTypes.TEXT}
                  size={ButtonSize.SMALL}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Main>
  );
};

export default SignUp;
